import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home } from 'lucide-react';
import { HelpArticleCard } from '@/components/help/HelpArticleCard';
import { PageLayout } from '@/components/ui/PageLayout';
import { useHelpCategories, useHelpArticles } from '@/hooks/useHelp';
import { TYPE } from '@/lib/typography';

export function HelpCategory() {
  const { t } = useTranslation();
  const { category: categorySlug } = useParams<{ category: string }>();

  const categoriesQuery = useHelpCategories();
  const articlesQuery = useHelpArticles();

  const categories = categoriesQuery.data ?? [];
  const allArticles = articlesQuery.data ?? [];

  const currentCategory = categories.find((c) => c.slug === categorySlug);
  const categoryArticles = allArticles.filter((a) => a.category_slug === categorySlug);

  if (categoriesQuery.isLoading || articlesQuery.isLoading) {
    return (
      <PageLayout>
        <div className="text-ds-muted">{t('app.loading', 'Indlæser...')}</div>
      </PageLayout>
    );
  }

  if (!currentCategory) {
    return (
      <PageLayout title={t('help.home', 'Hjælpecenter')}>
        <div className="rounded-card border border-ds-line bg-ds-card p-s7 text-center">
          <p className="text-ds-muted">{t('help.category_not_found', 'Kategori ikke fundet')}</p>
          <Link to="/help" className="mt-s4 inline-block text-ds-teal hover:text-ds-teal-700">
            {t('help.back_to_center', 'Tilbage til hjælpecenter')}
          </Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={currentCategory.title}
      subtitle={currentCategory.description}
    >
        <nav className={`mb-s5 flex items-center gap-s2 ${TYPE.small} text-ds-muted`}>
          <Link to="/help" className="flex items-center gap-s1 hover:text-ds-teal">
            <Home className="h-4 w-4" />
            {t('help.home', 'Hjælpecenter')}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-medium text-ds-text">{currentCategory.title}</span>
        </nav>
        <p className={`${TYPE.small} mb-s5 text-ds-muted`}>
          {t('help.article_count', { count: categoryArticles.length })}
        </p>

        {/* Articles list */}
        {categoryArticles.length === 0 ? (
          <div className="rounded-card border border-ds-line bg-ds-card p-s7 text-center">
            <p className="text-ds-muted">
              {t('help.no_articles', 'Ingen artikler i denne kategori endnu')}
            </p>
          </div>
        ) : (
          <div className="grid gap-s4 sm:grid-cols-2 lg:grid-cols-3">
            {categoryArticles.map((article) => (
              <HelpArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}

        <div className="mt-s7 border-t border-ds-line pt-s5">
          <h2 className={`${TYPE.heading} mb-s4 text-ds-text`}>
            {t('help.other_categories', 'Andre kategorier')}
          </h2>
          <div className="flex flex-wrap gap-s2">
            {categories
              .filter((c) => c.slug !== categorySlug)
              .map((category) => (
                <Link
                  key={category.id}
                  to={`/help/${category.slug}`}
                  className={`${TYPE.control} rounded-full border border-ds-field bg-ds-card px-s4 py-s2 font-medium text-ds-text transition-colors hover:border-ds-teal-line hover:bg-ds-teal-tint`}
                >
                  {category.title}
                </Link>
              ))}
          </div>
        </div>
    </PageLayout>
  );
}
